import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Funnel, Users, GlobeHemisphereWest } from "@phosphor-icons/react";
import CitySelect from "../components/CitySelect";
import HelpHint from "../components/HelpHint";
import * as HELP from "../lib/adminHelp";
import { COUNTRIES } from "../lib/worldCities";

const REGIONS = ["Punjab", "Sindh", "KPK", "Balochistan", "Islamabad Capital Territory", "GB", "AJK"];
const AGES = ["18-24", "25-34", "35-44", "45-54", "55+"];
const SECS = ["A", "B", "C", "D", "E"];
const GENDERS = ["Male", "Female", "Other"];
const INDUSTRIES = ["Tech / Software", "Banking & Finance", "Telecom", "FMCG / Retail", "Healthcare", "Education", "Government", "Agriculture", "Manufacturing", "Energy", "Media / Advertising"];
const EDUCATION = ["Primary", "Secondary", "Undergrad", "Postgrad", "PhD"];
const RESIDENCY = [
  { value: "", label: "Any" },
  { value: "pk", label: "Pakistan only" },
  { value: "overseas", label: "Overseas only" },
];

export default function AdminAudience() {
  const [filter, setFilter] = useState({ gender: "", region: "", age_band: "", sec: "", city: "", industry: "", education: "", residency: "", country: "", sectors: [], lifestyle_filter: {} });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sectorOpts, setSectorOpts] = useState([]); // [{key,label}]
  // DNA fields list — drives the dynamic lifestyle filter chips + the
  // dynamic breakdown cards. Fetched once on mount.
  const [dnaFields, setDnaFields] = useState([]);

  useEffect(() => {
    api.get("/sectors").then((r) => setSectorOpts(r.data || [])).catch(() => {});
    api.get("/admin/profile-dna/public").then((r) => setDnaFields(r.data || [])).catch(() => {});
  }, []);

  // Toggle a single value within filter.lifestyle_filter[fieldKey]. Multi-select
  // semantics: re-clicking removes; never restricts to one. Empty arrays are
  // pruned so the API payload stays small.
  const toggleLifestyle = (fieldKey, value) => {
    const cur = filter.lifestyle_filter[fieldKey] || [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    const lf = { ...filter.lifestyle_filter };
    if (next.length) lf[fieldKey] = next;
    else delete lf[fieldKey];
    setFilter({ ...filter, lifestyle_filter: lf });
  };

  // DNA fields exposed as filter chips: must be non-hidden, must have at least
  // 2 picker options. Fields like `cnic` / free-text `profession` are skipped
  // because they're identity values, not bucketable dimensions.
  const dnaFilterFields = dnaFields.filter((f) => !f.hidden && (f.options || []).length >= 2 && (f.kind === "pill_single" || f.kind === "pill_multi" || f.kind === "dropdown"));

  // Lifestyle keys for which to request breakdown cards — same allowlist as the
  // filterable fields, so every dimension you CAN slice on is also one you can
  // SEE the distribution of in the result.
  const breakdownLifestyleKeys = dnaFilterFields.map((f) => f.key);

  const run = async () => {
    setBusy(true);
    const cleaned = Object.fromEntries(Object.entries(filter).filter(([k, v]) => {
      if (k === "lifestyle_filter") return Object.keys(v || {}).length > 0;
      return v && (Array.isArray(v) ? v.length : true);
    }));
    cleaned.breakdown_lifestyle_keys = breakdownLifestyleKeys;
    try {
      const { data } = await api.post("/admin/audience-query", cleaned);
      setResult(data);
    } finally { setBusy(false); }
  };
  // Auto-run baseline query once DNA fields land so the user lands on a
  // populated dashboard. Re-runs only on DNA list change (not on every filter
  // edit — the Run-query button drives that).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { run(); }, [dnaFields.length]);

  const toggleSector = (k) => setFilter({ ...filter, sectors: filter.sectors.includes(k) ? filter.sectors.filter((x) => x !== k) : [...filter.sectors, k] });

  return (
    <div>
      <div className="border-b-2 border-black pb-4 mb-6">
        <div className="overline flex items-center gap-1">AUDIENCE EXPLORER <HelpHint text={HELP.PAGE.audience} /></div>
        <h1 className="text-4xl font-black tracking-tighter">Live panel sizing</h1>
        <p className="text-sm text-neutral-600 mt-2 max-w-3xl">{HELP.AUDIENCE.intro}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border-2 border-black p-6 space-y-5" data-testid="audience-filters">
          <div className="overline flex items-center gap-2"><Funnel size={14} /> FILTER PANELISTS</div>

          {/* Residency / Country — surfaced as the first filter to keep the
              overseas vs Pakistan dimension front-of-mind. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pb-5 border-b border-neutral-200">
            <div>
              <Label className="overline mb-2 block flex items-center gap-1"><GlobeHemisphereWest size={12} /> residency</Label>
              <div className="flex flex-wrap gap-1.5">
                {RESIDENCY.map((r) => (
                  <button
                    key={r.value || "any"}
                    onClick={() => setFilter({ ...filter, residency: r.value, country: "" })}
                    data-testid={`f-residency-${r.value || "any"}`}
                    className={`px-3 py-1 text-xs border ${filter.residency === r.value ? 'bg-black text-white' : 'border-neutral-300 hover:border-black'}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="overline mb-2 block">specific country (optional)</Label>
              <select
                data-testid="f-country"
                value={filter.country}
                onChange={(e) => setFilter({ ...filter, country: e.target.value, residency: e.target.value ? "" : filter.residency })}
                className="border-2 border-neutral-300 px-3 py-2 w-full text-sm"
              >
                <option value="">Any country</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Pill name="gender" value={filter.gender} set={(v) => setFilter({ ...filter, gender: v })} options={GENDERS} />
            <Pill name="region" value={filter.region} set={(v) => setFilter({ ...filter, region: v })} options={REGIONS} />
            <Pill name="age_band" value={filter.age_band} set={(v) => setFilter({ ...filter, age_band: v })} options={AGES} />
            <Pill name="sec" value={filter.sec} set={(v) => setFilter({ ...filter, sec: v })} options={SECS} />
            <Pill name="education" value={filter.education} set={(v) => setFilter({ ...filter, education: v })} options={EDUCATION} />
            <div>
              <Label className="overline mb-2 block">city</Label>
              <CitySelect value={filter.city} onChange={(v) => setFilter({ ...filter, city: v })} testid="f-city" />
            </div>
            <div className="md:col-span-2">
              <Label className="overline mb-2 block">industry</Label>
              <select value={filter.industry} onChange={(e) => setFilter({ ...filter, industry: e.target.value })} className="border-2 border-neutral-300 px-3 py-2 w-full">
                <option value="">Any</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="overline mb-2 block">sectors of interest (any-of)</Label>
              <p className="text-xs text-neutral-500 mb-2">Sector match uses the same logic as wave visibility — a panelist matches if ANY of their picked sectors is in your selection. Leave empty for &quot;any sector&quot;.</p>
              <div className="flex flex-wrap gap-1.5">
                {sectorOpts.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => toggleSector(s.key)}
                    data-testid={`f-sector-${s.key}`}
                    className={`px-3 py-1 text-xs border ${filter.sectors.includes(s.key) ? 'bg-mango text-black border-black' : 'border-neutral-300 hover:border-black'}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* DNA-driven lifestyle filter — auto-generated from
              profile_dna_fields. Every non-hidden field with options becomes
              its own multi-select chip group. This is the unlock for queries
              like "car owners earning 100-200k in Punjab using HBL" — i.e. the
              actual saleable insight surface for enterprise clients. */}
          {dnaFilterFields.length > 0 && (
            <div className="pt-5 border-t-2 border-neutral-200" data-testid="audience-dna-filters">
              <div className="overline mb-1 text-mango">PROFILE DNA — LIFESTYLE FILTERS</div>
              <p className="text-xs text-neutral-500 mb-4">
                Pick any combination of lifestyle answers. Multi-select within a field = OR, across fields = AND.
                Auto-generated from <span className="font-mono">Site Content → Profile DNA</span> — every new DNA field appears here automatically.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {dnaFilterFields.map((f) => {
                  const selected = filter.lifestyle_filter[f.key] || [];
                  return (
                    <div key={f.key} data-testid={`f-dna-${f.key}`}>
                      <Label className="overline mb-2 block">{f.label || f.key}</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {(f.options || []).map((opt) => {
                          const sel = selected.includes(opt);
                          return (
                            <button
                              key={opt}
                              onClick={() => toggleLifestyle(f.key, opt)}
                              data-testid={`f-dna-${f.key}-${(opt || "").toString().replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              className={`px-3 py-1 text-xs border ${sel ? 'bg-mango text-black border-black' : 'border-neutral-300 hover:border-black'}`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="pt-4 border-t flex gap-2">
            <Button data-testid="audience-run" onClick={run} disabled={busy} className="bg-mango hover:bg-mango/90 text-white border-2 border-black rounded-none">{busy ? "Counting…" : "Run query"}</Button>
            <Button variant="ghost" onClick={() => setFilter({ gender: "", region: "", age_band: "", sec: "", city: "", industry: "", education: "", residency: "", country: "", sectors: [], lifestyle_filter: {} })} className="rounded-none">Reset</Button>
          </div>
        </div>

        <div className="bg-black text-white p-6 sticky top-20 self-start" data-testid="audience-result">
          <div className="overline text-mango flex items-center gap-2"><Users size={14} /> MATCHING PANELISTS</div>
          <div className="font-mono text-7xl font-bold mt-3">{result?.count ?? "—"}</div>
          <div className="font-mono text-xs text-neutral-400 mt-3">of {result?.total_panel ?? "—"} complete profiles</div>
          {result && <div className="font-mono text-2xl text-mango mt-2">{result.share_pct}% share</div>}
          {result && (result.overseas_count > 0 || result.pk_count > 0) && (
            <div className="mt-4 pt-4 border-t border-neutral-800 grid grid-cols-2 gap-3 text-xs" data-testid="audience-pk-overseas-split">
              <div>
                <div className="overline text-neutral-400">IN PAKISTAN</div>
                <div className="font-mono text-2xl mt-1">{result.pk_count ?? "—"}</div>
              </div>
              <div>
                <div className="overline text-neutral-400">OVERSEAS</div>
                <div className="font-mono text-2xl mt-1 text-mango">{result.overseas_count ?? "—"}</div>
              </div>
            </div>
          )}
          <div className="mt-6 text-xs text-neutral-400">Use this to size niche surveys before fielding. Set audience to &quot;niche&quot; on a question and reapply these filters to target only matching panelists.</div>
        </div>
      </div>

      {/* Breakdown cards — render when we have data */}
      {result && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="audience-breakdowns">
          <BreakdownCard title="By sector of interest" rows={result.breakdown_by_sector} keyField="sector" />
          <BreakdownCard title="By country" rows={result.breakdown_by_country} keyField="country" />
          <BreakdownCard title="By city (top 30)" rows={result.breakdown_by_city} keyField="city" />
          <BreakdownCard title="By region (Pakistan)" rows={result.breakdown_by_region} keyField="region" />
          <BreakdownCard title="By age band" rows={result.breakdown_by_age} keyField="age_band" />
          {/* DNA-driven breakdowns — one card per filterable lifestyle field,
              showing the live distribution within the current filtered cohort.
              This is what makes the explorer actually saleable: a client can
              see "of 21,300 matching patrons → 9,800 own a car, 6,200 bank
              with HBL, 4,100 have solar" without us writing any custom code. */}
          {dnaFilterFields.map((f) => (
            <BreakdownCard
              key={f.key}
              title={`By ${(f.label || f.key).toLowerCase()}`}
              rows={(result.breakdown_by_lifestyle?.[f.key] || []).map((r) => ({ [f.key]: r.value, n: r.n }))}
              keyField={f.key}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Pill({ value, set, options, name }) {
  return (
    <div>
      <Label className="overline mb-2 block">{name}</Label>
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => set("")} className={`px-3 py-1 text-xs border ${value === "" ? 'bg-black text-white' : 'border-neutral-300'}`}>Any</button>
        {options.map((o) => (
          <button key={o} onClick={() => set(o)} data-testid={`f-${name}-${o}`} className={`px-3 py-1 text-xs border ${value === o ? 'bg-black text-white' : 'border-neutral-300 hover:border-black'}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}

function BreakdownCard({ title, rows, keyField }) {
  const data = (rows || []).filter((r) => r[keyField]).slice(0, 30);
  const total = data.reduce((acc, r) => acc + (r.n || 0), 0);
  return (
    <div className="bg-white border-2 border-black" data-testid={`breakdown-${keyField}`}>
      <div className="bg-black text-white px-4 py-2 overline flex items-center justify-between">
        <span>{title}</span>
        <span className="text-[10px] font-mono text-neutral-400">{data.length} buckets · n={total}</span>
      </div>
      <div className="p-4 max-h-80 overflow-y-auto">
        {data.length === 0 && <div className="text-xs text-neutral-400 italic">No matching panelists.</div>}
        {data.map((r) => {
          const pct = total ? Math.round((r.n / total) * 100) : 0;
          return (
            <div key={r[keyField]} className="flex items-center gap-2 mb-1 text-xs" data-testid={`breakdown-row-${(r[keyField] || "").toString().replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              <span className="font-mono w-40 truncate" title={r[keyField]}>{r[keyField] || "(none)"}</span>
              <div className="flex-1 bg-neutral-100 h-4 relative">
                <div className="bg-mango h-full" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-mono w-12 text-right">{r.n}</span>
              <span className="font-mono w-10 text-right text-neutral-500">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
